import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import QuestionListViewModel from 'jobs/questions/QuestionListViewModel'
import { task } from 'mobx-task'
import { CancellationToken } from 'infra/cancellation'
import { QuestionsQuerySession } from 'jobs/QuestionStore'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { toStream } from 'utils/mobx-util'
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators'
import { unpair, whileActive, withCancellation } from 'utils/rx-util'
import Toggle from 'infra/Toggle'
import { action, computed, observable, makeObservable, runInAction } from 'mobx'
import orderBy from 'lodash/orderBy'
import TabController from 'infra/TabController'
import AmendmentsTableViewModel from '../AmendmentsCard/AmendmentsTable/AmendmentsTableViewModel'
import InfiniteScrollProvider from '../../../../../utils/InfiniteScrollProvider'
import { Id } from '../../../../../types/definitions'

/**
 * Details view model.
 */
export default class DetailsViewModel {
  /**
   * Tracks active status.
   * @private
   */
  private _isActive = new BehaviorSubject(false)

  /**
   * The amendments list.
   */
  amendmentsTableVM = new AmendmentsTableViewModel(this.job)

  /**
   * The responses (questions).
   */
  responses: QuestionListViewModel

  /**
   * Whether the Responses section is showing.
   */
  responsesSection = new Toggle()

  /**
   * Whether the complete onboarding is showed.
   */
  completeOnboarding = new Toggle()

  /**
   * Whether the questions have been completed.
   */
  @observable
  questionsCompleted = false

  /**
   * Event filter.
   */
  @observable eventFilter = EventFilter.All

  /**
   * TabController for the Captured Information section tabs.
   */
  capturedInformationTabController = new TabController<CapturedInformationTab>(
    () => [
      {
        tab: CapturedInformationTab.Questions,
        title: 'Questions',
        hasError: () => false,
      },
      {
        tab: CapturedInformationTab.Notes,
        title: 'Notes',
        hasError: () => false,
      },
      {
        tab: CapturedInformationTab.BusinessInfo,
        title: 'Business',
        hasError: () => false,
      },
      {
        tab: CapturedInformationTab.PersonalInfo,
        title: 'Personal',
        hasError: () => false,
      },
      {
        tab: CapturedInformationTab.RefundInfo,
        title: 'Refund',
        hasError: () => false,
      },
    ],
  )

  informationTabController = new TabController<ResponsesInformationTab>(() => [
    {
      tab: ResponsesInformationTab.Questions,
      title: 'Questions',
      hasError: () => false,
    },
    {
      tab: ResponsesInformationTab.Notes,
      title: 'Notes',
      hasError: () => false,
    },
    {
      tab: ResponsesInformationTab.PersonalInfo,
      title: 'Personal',
      hasError: () => false,
    },
    {
      tab: ResponsesInformationTab.BusinessInfo,
      title: 'Business',
      hasError: () => false,
    },
    {
      tab: ResponsesInformationTab.RefundInfo,
      title: 'Refund',
      hasError: () => false,
    },
  ])

  jobEventsScrollProvider = new InfiniteScrollProvider({
    startFetchTaskAsPending: true,
    fetchItems: (params) =>
      this.jobStore.fetchJobEvents(
        Id.assert(this.job.id),
        params.limit,
        params.after,
      ),
    limit: 100,
  })

  constructor(
    public job: Job,
    private jobStore: JobStore,
    private questionsQuerySession: QuestionsQuerySession,
    createQuestionListViewModel: () => QuestionListViewModel,
    private _useEditDraftJobForFirmAdmin: () => boolean,
  ) {
    makeObservable(this)
    this.responses = createQuestionListViewModel()

    // Ensures that Responses is kept up-to-date.
    // We don't dispose the subscription because we want it to be cached.
    combineLatest([
      // Observe changes to job answers, emit current answers immediately
      toStream(() => this.job.answers, true),
      // Combine that with whether we are showing the section.
      this.responsesSection.isEnabled$,
      this.completeOnboarding.isEnabled$,
    ])
      .pipe(
        // When showing the section..
        filter(
          ([, showingResponses, showingCompleteOnboarding]) =>
            showingResponses || showingCompleteOnboarding,
        ),
        unpair(),
        // And if the answers have changed since the last fetch..
        distinctUntilChanged(),
        // Fetch the responses.
        switchMap(() => withCancellation(this.fetchResponses)),
        // Ensures this only runs while the VM is active.
        whileActive(this._isActive),
      )
      .subscribe()
  }

  /**
   * All job events.
   */
  @computed
  get allEvents() {
    return this.jobStore.eventsForJob(this.job)
  }

  /**
   * Job events filtered.
   */
  @computed
  get filteredEvents() {
    const eventFilter = this.eventFilter
    const filtered = this.allEvents.filter((e) => {
      switch (eventFilter) {
        case EventFilter.All:
          return true
        default:
          return (
            (eventFilter === EventFilter.TaskUpdates) ===
            (e.eventType === 'TASK_MARKED_COMPLETE' ||
              e.eventType === 'TASK_MARKED_INCOMPLETE')
          )
      }
    })

    return orderBy(filtered, ['dateCreated'], ['desc'])
  }

  /**
   * Whether the current user is allowed to view the Captured information card.
   */
  @computed
  get canViewCapturedInfoCardAsAdmin() {
    return this.job.canViewCapturedInfoCardAsAdmin
  }

  /**
   * Whether the current member is able to edit the draft in job details
   */
  @computed
  get useEditDraftJobForFirmAdmin() {
    return this._useEditDraftJobForFirmAdmin()
  }

  @computed
  get canViewCapturedInfoCard() {
    return (
      this.canViewCapturedInfoCardAsAdmin || this.useEditDraftJobForFirmAdmin
    )
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.jobEventsScrollProvider.fetch().then()
    this._isActive.next(true)
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._isActive.next(false)
  }

  /**
   * Sets the event filter.
   *
   * @param filter
   */
  @action.bound
  setEventFilter(filter: EventFilter) {
    this.eventFilter = filter
  }

  /**
   * Fetches questions and variables responses and updates them.
   */
  fetchResponses = task(async (ct: CancellationToken) => {
    const result = await ct.race(
      this.questionsQuerySession.fetchQuestions(
        this.job.answers,
        this.job.canViewQuestionVariables,
      ),
    )
    if (result) {
      runInAction(() => {
        this.responses.reconcile(result.questions)
        this.questionsCompleted = result.done
      })
    }
  })
}

/**
 * Event filter.
 */
export enum EventFilter {
  /**
   * View all events.
   */
  All,
  /**
   * View only events related to job updates.
   */
  JobUpdates,
  /**
   * View only events related to task updates.
   */
  TaskUpdates,
}

/**
 * Captured Information section tab.
 */
export const enum CapturedInformationTab {
  Questions,
  Notes,
  BusinessInfo,
  PersonalInfo,
  RefundInfo,
}

export const enum ResponsesInformationTab {
  Questions,
  Notes,
  PersonalInfo,
  BusinessInfo,
  RefundInfo,
}
